
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Building2, FileText, Loader2, Edit, Folder, FolderPlus, Plus, CheckSquare, Square, Download, X, RefreshCw, Paperclip, Users, StickyNote, UserCheck, Image as ImageIcon, Scale, PanelLeft, Info, Edit2, CheckCircle, AlertTriangle, AlertCircle, MoreHorizontal, Eye, Trash2, Columns, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { StatusBadge } from '../badges/StatusBadge';
import { SyncStatusBadge } from '../badges/SyncStatusBadge';
import { useNavigate } from 'react-router-dom';

export const TrademarksTable = ({
    trademarks = [],
    loading = false,
    selectedIds = new Set(),
    onSelectionChange,
    onSelectAll, // Should handle "page" or "all" logic if passed, or simple toggle
    selectAllMode, // 'page', 'all', or null
    onClearSelection,
    sortConfig = { key: 'denominacion', direction: 'asc' },
    onSort,
    storageKey = 'trademarks_visible_columns',
    showClientColumn = true,
    showFolderColumn = false,
    showActions = true,
    isCliente = false,
    isAdmin = false,
    actionsRenderer, // Function(tm) -> ReactNode (Dropdown items)
    statusCatalog = [], // For inline edit status
    onUpdateStatus, // Function(id, statusId)
    onUpdateFallbackName, // Function(id, fallbackName)
    onAssignClient, // Function(tmId, clientId | null) -> Promise
    clients = [], // For inline client assignment
    getFolderName, // Optional function
    getClientName, // Optional function
    onRowClick, // Optional function
}) => {
    const navigate = useNavigate();

    // Column Visibility State
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = localStorage.getItem(storageKey);
        // Default columns
        const defaults = {
            img: true,
            denominacion: true,
            expediente: true,
            registro: true,
            fecha_vigencia: true, // New standard
            cliente: showClientColumn,
            folder: showFolderColumn,
            estatus_impi: true,
            estatus_legal: true,
            sync: true,
            actions: true
        };
        return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
    });

    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
    }, [visibleColumns, storageKey]);

    const getImageUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('/api')) {
            const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
            const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            return `${cleanBase}${url}`;
        }
        return url;
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    // Sync State Helper
    const getSyncState = (tm) => {
        if (!tm.last_sync_at) return { status: 'never', label: 'Nunca', color: 'bg-gray-100 text-gray-800' };
        if (tm.has_pending_changes) return { status: 'pending', label: 'Pendiente', color: 'bg-amber-100 text-amber-800' };
        if (tm.last_sync_status === 'error') return { status: 'error', label: 'Error', color: 'bg-red-100 text-red-800' };
        if (tm.last_sync_status === 'warning') return { status: 'warning', label: 'Parcial', color: 'bg-orange-100 text-orange-800' };
        return { status: 'success', label: 'Actualizado', color: 'bg-emerald-100 text-emerald-800' };
    };

    const formatLastSync = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('es-MX', {
            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="p-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-900 mx-auto" />
            </div>
        );
    }

    if (!trademarks || trademarks.length === 0) {
        return (
            <div className="p-12 text-center text-gray-500">
                No se encontraron marcas
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="hidden lg:block bg-white rounded-lg border border-gray-200 overflow-x-auto relative min-h-[400px]">
                <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50/50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                        <tr>
                            {/* Sticky Checkbox */}
                            <th className="sticky left-0 z-20 bg-gray-50 px-4 py-3 w-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                {!onSelectAll ? (
                                    // Simple Select All Checkbox logic
                                    <Checkbox
                                        checked={trademarks.length > 0 && selectedIds.size === trademarks.length}
                                        onCheckedChange={() => {
                                            // Default simple toggle behavior if no special onSelectAll handler
                                            if (selectedIds.size === trademarks.length) {
                                                onClearSelection && onClearSelection();
                                            } else {
                                                // Need a way to select all IDs. 
                                                // If callback is strictly `onSelectAll(bool)`, we use that.
                                            }
                                        }}
                                    />
                                ) : (
                                    // Use Dropdown if onSelectAll is complex (Page vs All) or simple Checkbox if we just pass a function
                                    selectAllMode !== undefined ? (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button className="flex items-center gap-1 hover:bg-gray-100 rounded p-1 transition-colors outline-none">
                                                    {selectAllMode === 'page' || selectAllMode === 'all' || (trademarks.length > 0 && selectedIds.size === trademarks.length) ? (
                                                        <CheckSquare className="h-4 w-4 text-primary" />
                                                    ) : selectedIds.size > 0 ? (
                                                        <div className="h-4 w-4 border-2 border-primary rounded bg-primary/20"></div>
                                                    ) : (
                                                        <Square className="h-4 w-4 text-gray-400" />
                                                    )}
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="start">
                                                <DropdownMenuItem onClick={() => onSelectAll('page')}>
                                                    Seleccionar página ({trademarks.length})
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => onSelectAll('filtered')}>
                                                    Seleccionar todos (Filtrados)
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem onClick={onClearSelection}>
                                                    Limpiar selección
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    ) : (
                                        // Simple checkbox
                                        <Checkbox
                                            checked={trademarks.length > 0 && selectedIds.size === trademarks.length}
                                            onCheckedChange={onSelectAll}
                                        />
                                    )
                                )}
                            </th>

                            {visibleColumns.img && (
                                <th className="px-3 py-3 w-16 text-center">
                                    <ImageIcon className="h-4 w-4 mx-auto" />
                                </th>
                            )}

                            {visibleColumns.denominacion && (
                                <th
                                    className="px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors min-w-[180px]"
                                    onClick={() => onSort && onSort('denominacion')}
                                >
                                    Denominación {getSortIcon('denominacion')}
                                </th>
                            )}

                            {visibleColumns.expediente && (
                                <th
                                    className="px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors w-24"
                                    onClick={() => onSort && onSort('expediente')}
                                >
                                    EXP {getSortIcon('expediente')}
                                </th>
                            )}

                            {visibleColumns.registro && (
                                <th
                                    className="px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors w-24"
                                    onClick={() => onSort && onSort('registro')}
                                >
                                    REG {getSortIcon('registro')}
                                </th>
                            )}

                            {visibleColumns.fecha_vigencia && (
                                <th
                                    className="px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors w-28"
                                    onClick={() => onSort && onSort('fecha_vigencia')}
                                >
                                    VIG {getSortIcon('fecha_vigencia')}
                                </th>
                            )}

                            {visibleColumns.cliente && showClientColumn && (
                                <th
                                    className="px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
                                    onClick={() => onSort && onSort('client_id')}
                                >
                                    Cliente {getSortIcon('client_id')}
                                </th>
                            )}

                            {visibleColumns.folder && showFolderColumn && (
                                <th className="px-4 py-3">Carpeta</th>
                            )}

                            {visibleColumns.estatus_impi && (
                                <th className="px-4 py-3 w-32">
                                    <div className="flex items-center gap-1">
                                        Estatus IMPI
                                    </div>
                                </th>
                            )}

                            {visibleColumns.estatus_legal && (
                                <th
                                    className="px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors w-32"
                                    onClick={() => onSort && onSort('estatus_interno')}
                                >
                                    Estatus Legal {getSortIcon('estatus_interno')}
                                </th>
                            )}

                            {visibleColumns.sync && (
                                <th className="px-4 py-3 w-40">
                                    Actualización IMPI
                                </th>
                            )}

                            {showActions && !isCliente && (
                                <th className="sticky right-0 z-20 bg-gray-50 px-4 py-3 text-right shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                    Acciones
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {trademarks.map((tm) => (
                            <tr
                                key={tm.id}
                                onClick={(e) => {
                                    if (!onRowClick && (e.ctrlKey || e.metaKey)) {
                                        window.open(`/trademarks/${tm.id}`, '_blank');
                                    } else {
                                        onRowClick ? onRowClick(tm) : navigate(`/trademarks/${tm.id}`);
                                    }
                                }}
                                onAuxClick={(e) => {
                                    if (e.button === 1 && !onRowClick) {
                                        window.open(`/trademarks/${tm.id}`, '_blank');
                                    }
                                }}
                                className={`group hover:bg-gray-50/80 cursor-pointer transition-colors ${selectedIds.has(tm.id) ? 'bg-indigo-50/60' : ''}`}
                            >
                                {/* Sticky Checkbox Cell */}
                                <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50/80 px-4 py-3 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        checked={selectedIds.has(tm.id)}
                                        onCheckedChange={() => onSelectionChange && onSelectionChange(tm.id)}
                                    />
                                </td>

                                 {visibleColumns.img && (
                                    <td className="px-3 py-3">
                                        {tm.imagen_url || tm.imagen_local_path ? (
                                            <TooltipProvider delayDuration={0}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div className="relative w-12 h-12 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 overflow-hidden cursor-zoom-in hover:border-blue-300 transition-colors">
                                                            <img
                                                                src={getImageUrl(tm.imagen_local_path || tm.imagen_url)}
                                                                alt={tm.denominacion}
                                                                className="w-full h-full object-contain p-1 relative z-10"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    const parent = e.target.parentElement;
                                                                    if (parent) {
                                                                        parent.style.cursor = 'default';
                                                                        parent.classList.remove('hover:border-blue-300');
                                                                        // Show placeholder that is already there behind
                                                                        const placeholder = parent.querySelector('.img-placeholder');
                                                                        if (placeholder) placeholder.style.display = 'flex';
                                                                    }
                                                                }}
                                                            />
                                                            <div 
                                                                className="img-placeholder absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-200"
                                                                style={{ display: 'none' }}
                                                            >
                                                                <span className="text-sm font-bold text-gray-400 select-none">
                                                                    {(tm.denominacion || tm.fallback_name || '?').charAt(0).toUpperCase()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="right" className="p-0 border-0 overflow-hidden rounded-lg shadow-2xl bg-white w-64 h-64 flex items-center justify-center z-50">
                                                        <img
                                                            src={getImageUrl(tm.imagen_local_path || tm.imagen_url)}
                                                            alt="Vista completa"
                                                            className="w-full h-full object-contain p-2"
                                                            onError={(e) => {
                                                                const parent = e.target.parentElement;
                                                                if (parent) {
                                                                    parent.innerHTML = `<span class="px-4 text-center text-gray-400 font-medium">${tm.tipo ? (tm.tipo.toLowerCase() === 'aviso comercial' ? 'Aviso Comercial' : 'Marca Nominativa') : 'Marca Nominativa'}</span>`;
                                                                }
                                                            }}
                                                        />
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        ) : (
                                            <div className="relative w-12 h-12 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-200 rounded-lg border border-gray-100 overflow-hidden text-sm font-bold text-gray-400 select-none">
                                                {(tm.denominacion || tm.fallback_name || '?').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                    </td>
                                )}

                                {visibleColumns.denominacion && (
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col">
                                            <span className={`text-sm font-semibold transition-colors ${!tm.denominacion ? 'text-gray-500 italic group-hover:text-blue-500' : 'text-gray-900 group-hover:text-blue-600'}`}>
                                                {tm.denominacion || 'Sin denominación'}
                                            </span>
                                            {!tm.denominacion && (
                                                <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="text"
                                                        className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-2 py-0.5 w-full hover:bg-white hover:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:bg-white transition-all placeholder-indigo-300"
                                                        placeholder="Nombre de Referencia..."
                                                        defaultValue={tm.fallback_name || ''}
                                                        onBlur={(e) => {
                                                            const newVal = e.target.value.trim();
                                                            if (newVal !== (tm.fallback_name || '')) {
                                                                if (onUpdateFallbackName) {
                                                                    onUpdateFallbackName(tm.id, newVal);
                                                                } else {
                                                                    const event = new CustomEvent('trademark-fallback-update', {
                                                                        detail: { id: tm.id, fallback_name: newVal }
                                                                    });
                                                                    window.dispatchEvent(event);
                                                                }
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.target.blur();
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            {tm.tipo && <span className="text-xs text-gray-500 mt-1">{tm.tipo}</span>}
                                            {(tm.clase || tm.clases) && <span className="text-xs text-gray-400">Clases {tm.clase || tm.clases}</span>}
                                        </div>
                                    </td>
                                )}

                                {visibleColumns.expediente && (
                                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                                        {tm.expediente || '-'}
                                    </td>
                                )}

                                {visibleColumns.registro && (
                                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                                        {tm.registro || <span className="text-gray-400 text-xs italic">Pendiente</span>}
                                    </td>
                                )}

                                {visibleColumns.fecha_vigencia && (
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                        {tm.fecha_vigencia ? new Date(tm.fecha_vigencia).toLocaleDateString() : '-'}
                                    </td>
                                )}

                                {visibleColumns.cliente && showClientColumn && (
                                    <td className="px-4 py-3 text-sm text-gray-600 group/client" onClick={(e) => e.stopPropagation()}>
                                        {onAssignClient && !isCliente ? (
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <button className={`flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors text-left w-full ${tm.client_id
                                                        ? 'hover:bg-gray-100 text-gray-700'
                                                        : 'hover:bg-blue-50 text-blue-500 border border-dashed border-blue-200'
                                                        }`}>
                                                        {tm.client_id ? (
                                                            <>
                                                                <Building2 className="h-3 w-3 text-gray-400 shrink-0" />
                                                                <span className="truncate max-w-[120px] text-xs font-medium">{getClientName ? getClientName(tm.client_id) : (tm.client_name || tm.client_id)}</span>
                                                                <Edit2 className="h-2.5 w-2.5 text-gray-300 opacity-0 group-hover/client:opacity-100 ml-auto shrink-0" />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <UserPlus className="h-3 w-3 shrink-0" />
                                                                <span className="text-xs">Asignar</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-56 p-3" onClick={(e) => e.stopPropagation()}>
                                                    <div className="space-y-3">
                                                        <h4 className="font-semibold text-xs text-gray-900 border-b pb-2">Asignar cliente</h4>
                                                        <Select
                                                            defaultValue={tm.client_id || 'none'}
                                                            onValueChange={(val) => onAssignClient(tm.id, val === 'none' ? null : val)}
                                                        >
                                                            <SelectTrigger className="h-8 text-xs w-full">
                                                                <SelectValue placeholder="Seleccionar cliente..." />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="none" className="text-gray-400 text-xs">Sin cliente</SelectItem>
                                                                {clients.map(c => (
                                                                    <SelectItem key={c.id} value={c.id} className="text-xs">{c.nombre}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </PopoverContent>
                                            </Popover>
                                        ) : (
                                            <span>{getClientName ? getClientName(tm.client_id) : (tm.client_name || <span className="text-gray-400 italic text-xs">Sin asignar</span>)}</span>
                                        )}
                                    </td>
                                )}

                                {visibleColumns.folder && showFolderColumn && (
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                        {tm.folder_id && (
                                            <div className="flex items-center gap-1">
                                                <Folder className="h-3 w-3 text-indigo-500" />
                                                <span>{getFolderName ? getFolderName(tm.folder_id) : 'Carpeta'}</span>
                                            </div>
                                        )}
                                    </td>
                                )}

                                {visibleColumns.estatus_impi && (
                                    <td className="px-4 py-3">
                                        <StatusBadge type="impi" value={tm.estatus_impi_crudo || tm.estatus} className="opacity-90" />
                                    </td>
                                )}

                                {visibleColumns.estatus_legal && (
                                    <td className="px-4 py-3 group/legal relative">
                                        <div className="flex items-center gap-2">
                                            <StatusBadge type="legal" statusId={tm.estatus_interno} catalog={statusCatalog} />
                                            {!isCliente && onUpdateStatus && (
                                                <Popover>
                                                    <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                        <button className="opacity-0 group-hover/legal:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-blue-600 focus:opacity-100 outline-none">
                                                            <Edit2 className="h-3 w-3" />
                                                        </button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-[240px] p-3" onClick={(e) => e.stopPropagation()}>
                                                        <div className="space-y-3">
                                                            <h4 className="font-medium text-xs text-gray-900 border-b pb-2">Editar Estatus Legal</h4>
                                                            <Select
                                                                defaultValue={tm.estatus_interno ? tm.estatus_interno.toString() : "none"}
                                                                onValueChange={(val) => onUpdateStatus(tm.id, val === "none" ? "" : val)}
                                                            >
                                                                <SelectTrigger className="h-8 text-xs w-full">
                                                                    <SelectValue placeholder="Seleccionar..." />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="none" className="text-gray-500">Sin estatus</SelectItem>
                                                                    {statusCatalog.map(st => (
                                                                        <SelectItem key={st.id} value={st.id.toString()} className="text-xs">
                                                                            {st.status_name}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </PopoverContent>
                                                </Popover>
                                            )}
                                        </div>
                                    </td>
                                )}

                                {visibleColumns.sync && (
                                    <td className="px-4 py-3">
                                        <SyncStatusBadge trademark={tm} />
                                    </td>
                                )}

                                {showActions && !isCliente && (
                                    <td className="sticky right-0 z-10 bg-white group-hover:bg-gray-50/80 px-4 py-3 text-right shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-3">
                                            {/* Compact Sync Indicator if column hidden */}
                                            {!visibleColumns.sync && (
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <div className="cursor-help flex items-center">
                                                                {(() => {
                                                                    const st = getSyncState(tm);
                                                                    if (st.status === 'success') return <CheckCircle className="h-4 w-4 text-emerald-500" />;
                                                                    if (st.status === 'warning') return <AlertTriangle className="h-4 w-4 text-orange-500" />;
                                                                    if (st.status === 'error') return <AlertCircle className="h-4 w-4 text-red-500" />;
                                                                    if (st.status === 'pending') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
                                                                    return <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />;
                                                                })()}
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="left" className="text-xs bg-gray-900 text-white border-0 z-50">
                                                            <p className="font-semibold mb-1">Actualización IMPI</p>
                                                            <p>Estado: {getSyncState(tm).label}</p>
                                                            <p className="text-gray-400">{formatLastSync(tm.last_sync_at)}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            )}

                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-gray-900">
                                                        <MoreHorizontal className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    {actionsRenderer ? actionsRenderer(tm) : (
                                                        <DropdownMenuItem onClick={() => navigate(`/trademarks/${tm.id}`)}>
                                                            <Eye className="h-4 w-4 mr-2" />
                                                            Ver detalle
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </td>
                                )}

                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile View (Cards) */}
            <div className="lg:hidden space-y-4">
                {trademarks.map((tm) => {
                    const syncState = getSyncState(tm);
                    return (
                        <div
                            key={tm.id}
                            onClick={() => onRowClick ? onRowClick(tm) : navigate(`/trademarks/${tm.id}`)}
                            className={`p-4 bg-white border rounded-xl shadow-sm transition-all active:scale-[0.98] ${selectedIds.has(tm.id) ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-gray-200'}`}
                        >
                            <div className="flex gap-4 items-start mb-3">
                                <div className="flex-shrink-0 relative">
                                    <div className="w-16 h-16 bg-gray-50 rounded-lg border flex items-center justify-center overflow-hidden">
                                        {(tm.imagen_local_path || tm.imagen_url) ? (
                                            <img
                                                src={getImageUrl(tm.imagen_local_path || tm.imagen_url)}
                                                alt={tm.denominacion}
                                                className="w-full h-full object-contain p-1 relative z-10"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div
                                            className="absolute inset-0 items-center justify-center bg-gradient-to-br from-gray-50 to-gray-200 flex"
                                            style={{ display: (tm.imagen_local_path || tm.imagen_url) ? 'none' : 'flex' }}
                                        >
                                            <span className="text-xl font-black text-gray-400 select-none">
                                                {(tm.denominacion || tm.fallback_name || tm.expediente || '?').charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="absolute -top-2 -left-2" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={selectedIds.has(tm.id)}
                                            onCheckedChange={() => onSelectionChange && onSelectionChange(tm.id)}
                                            className="h-5 w-5 bg-white shadow-sm"
                                        />
                                    </div>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-gray-900 truncate">
                                        {tm.denominacion || tm.fallback_name || 'Sin denominación'}
                                    </h3>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        <span className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                            Exp: {tm.expediente || '-'}
                                        </span>
                                        {tm.registro && (
                                            <span className="text-xs font-mono bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
                                                Reg: {tm.registro}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-2">
                                        <StatusBadge type="impi" value={tm.estatus_impi_crudo || tm.estatus} className="text-[10px]" />
                                    </div>
                                </div>
                                <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400">
                                                <MoreHorizontal className="h-5 w-5" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => navigate(`/trademarks/${tm.id}`)}>
                                                <Eye className="h-4 w-4 mr-2" /> Ver detalle
                                            </DropdownMenuItem>
                                            {isAdmin && !isCliente && (
                                                <DropdownMenuItem className="text-red-600">
                                                    <Trash2 className="h-4 w-4 mr-2" /> Eliminar
                                                </DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-y-3 pt-3 border-t border-gray-50">
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-gray-400 uppercase font-medium">Vigencia</span>
                                    <span className="text-xs text-gray-700 font-medium">
                                        {tm.fecha_vigencia ? new Date(tm.fecha_vigencia).toLocaleDateString() : 'Pendiente'}
                                    </span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-gray-400 uppercase font-medium">Estatus Legal</span>
                                    <StatusBadge type="legal" statusId={tm.estatus_interno} catalog={statusCatalog} className="text-[10px] w-fit" />
                                </div>
                                {showClientColumn && (
                                    <div className="flex flex-col col-span-2">
                                        <span className="text-[10px] text-gray-400 uppercase font-medium">Cliente</span>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            <Building2 className="h-3 w-3 text-gray-400" />
                                            <span className="text-xs text-gray-700 font-medium">{getClientName ? getClientName(tm.client_id) : (tm.client_name || '-')}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-3 pt-3 bg-gray-50/50 -mx-4 px-4 -mb-4 rounded-b-xl flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${syncState.color.replace('bg-', 'bg-').split(' ')[0]}`}></div>
                                    <span className="text-[10px] text-gray-500">Act: {formatLastSync(tm.last_sync_at)}</span>
                                </div>
                                <SyncStatusBadge trademark={tm} size="sm" showLabel={false} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
